import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import styled from 'styled-components';
import { rangeToDateRange, unixToJSDate } from './components/LineChart/utils';
import { get, getCurrentQuarter } from '../../services/engagement-okrs';
import {
  Panel,
  PanelHeader as Header,
  PanelContent as Content
} from '@workboard/wobo-ui';
import LineChart from './components/LineChart/line-chart';
import Title from './components/Title';
import T from './components/Toolbar';
import { v4 as uuid } from "uuid";

const classEmpty = 'empty', Empty = `.${classEmpty}`;
const titleId = uuid();

const ChartOKRs = ({ className, id, ...props }) => {
  const { t } = useTranslation();
  const mountedRef = useRef(true);
  const [isLoadingTimeframe, setLoadingTimeframe] = useState(true);
  const [timeframe, setTimeframe] = useState();
  const [currentQtr, setCurrentQtr] = useState();
  const [isLoadingTeams, setLoadingTeams] = useState(true);
  const [teams, setTeams] = useState([]);
  const [options, setOptions] = useState([]);

  const handleChangeTimeframe = useCallback(
    f => {
      setTimeframe(f);
    },
    [setTimeframe]
  );

  const handleResetTimeframe = useCallback(
    f => {
      setTimeframe(rangeToDateRange(currentQtr));
    },
    [currentQtr, setTimeframe]
  );

  useEffect(() => {
    async function fetchCurrentQuarter() {
      const response = await getCurrentQuarter(id);
      if (!mountedRef.current) return null;
      setCurrentQtr(response);
      setTimeframe(rangeToDateRange(response));
      setLoadingTimeframe(false);
    }

    if (id !== undefined) fetchCurrentQuarter();
  }, [id, setLoadingTimeframe, setCurrentQtr, setTimeframe]);

  useEffect(() => {
    async function fetchTeams() {
      const response = await get(id, timeframe);
      if (!mountedRef.current) return null;
      setTeams(response);
      setOptions({ min: unixToJSDate(response.min_date), max: unixToJSDate(response.max_date) });
      setLoadingTeams(false);
    }

    if (id !== undefined && timeframe) {
      fetchTeams();
    }
  }, [id, timeframe, setTeams, setLoadingTeams]);

  const Toolbar = useCallback(
    () => <T id={id} timeframe={timeframe} options={options} onChange={handleChangeTimeframe} onReset={handleResetTimeframe} />,
    [id, timeframe]
  );

  return (
    <Panel
      className={className}
      id={titleId}
      title={ <Title title={`Team Engagement on Current ${t("OKRs")}`}/> }
      toolbar={ timeframe ? Toolbar : '' }
      {...props}
      >
        <LineChart
          id={id}
          okrs={teams.okrs ? teams.okrs : []}
          range={timeframe}
          startDate={teams.start_date}
          endDate={teams.end_date}
          isLoading={id && (isLoadingTimeframe || isLoadingTeams)}
          classNameEmpty={classEmpty}
        />
    </Panel>
    );
};

export default styled(ChartOKRs)`
  padding: 0;

  ${Header} {
    margin: 8px 20px 0;
  }

  ${Content} {
    padding: 0;
    .highcharts-tooltip span{
      background: unset !important;
    }
  }

  ${Empty} {
    height: 400px;
    padding: 10px 0 0 20px;
  }
`;
